import { useEffect, useRef, useState, type TouchEvent } from "react";
import { Button } from "./Button";
import { Dialog } from "./Dialog";
import { Row, Stack } from "./Stack";

export interface FotoGaleria {
  id: number;
  miniatura: string;
  pantalla: string;
  alt: string;
  ancho: number;
  alto: number;
}

export interface GaleriaProps {
  etiqueta: string;
  fotos: FotoGaleria[];
  /** Miniaturas visibles; si hay más, la última muestra "+N". */
  maximo?: number;
}

const DESLIZAR_MINIMO = 40; // px de arrastre horizontal para cambiar de foto

/** Grilla de miniaturas que abre la foto en grande. Se navega con los botones, las flechas del teclado o deslizando. */
export function Galeria({ etiqueta, fotos, maximo = 4 }: GaleriaProps) {
  const [abierta, setAbierta] = useState<number | null>(null);
  const inicioToque = useRef<number | null>(null);
  const mover = (paso: number) => setAbierta((i) => (i === null ? i : Math.min(fotos.length - 1, Math.max(0, i + paso))));

  // Flechas del teclado: escuchamos en `window` (no en el diálogo) porque el foco se pierde
  // al desaparecer el botón "Siguiente"/"Anterior" en los extremos, y un handler local dejaría
  // de recibir el evento.
  useEffect(() => {
    if (abierta === null) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") mover(-1);
      if (e.key === "ArrowRight") mover(1);
    };
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierta]);

  if (fotos.length === 0) return null;

  const visibles = fotos.slice(0, maximo);
  const ocultas = fotos.length - visibles.length;
  const actual = abierta === null ? undefined : fotos[abierta];
  const hayAnterior = abierta !== null && abierta > 0;
  const haySiguiente = abierta !== null && abierta < fotos.length - 1;

  const alTocar = (e: TouchEvent) => {
    inicioToque.current = e.touches[0]?.clientX ?? null;
  };
  const alSoltar = (e: TouchEvent) => {
    const fin = e.changedTouches[0]?.clientX;
    if (inicioToque.current === null || fin === undefined) return;
    const arrastre = fin - inicioToque.current;
    if (Math.abs(arrastre) >= DESLIZAR_MINIMO) mover(arrastre < 0 ? 1 : -1);
    inicioToque.current = null;
  };

  return (
    <>
      <ul aria-label={etiqueta} className="grid grid-cols-4 gap-1.5">
        {visibles.map((f, i) => {
          const conResto = ocultas > 0 && i === visibles.length - 1;
          return (
            <li key={f.id}>
              <button
                type="button"
                aria-label={`Ver foto ${i + 1} de ${fotos.length}: ${f.alt}${conResto ? ` (y ${ocultas} más)` : ""}`}
                onClick={() => setAbierta(i)}
                className="relative block aspect-[4/3] w-full overflow-hidden rounded-control bg-superficie-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primario"
              >
                <img src={f.miniatura} alt={f.alt} loading="lazy" width={f.ancho} height={f.alto} className="size-full object-cover" />
                {conResto && (
                  <span aria-hidden className="absolute inset-0 grid place-items-center bg-black/50 text-subtitulo font-semibold text-texto-invertido">
                    +{ocultas}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      <Dialog
        abierto={actual !== undefined}
        onCambio={(abierto) => !abierto && setAbierta(null)}
        titulo={abierta === null ? "Foto" : `Foto ${abierta + 1} de ${fotos.length}`}
        ancho="amplio"
      >
        {actual && (
          <Stack espacio={3} onTouchStart={alTocar} onTouchEnd={alSoltar}>
            <img src={actual.pantalla} alt={actual.alt} width={actual.ancho} height={actual.alto} className="max-h-[70dvh] w-full rounded-control object-contain" />
            {/* Leyenda visual: el alt de la imagen ya da el mismo texto a los lectores de pantalla; sin esto lo anunciarían dos veces. */}
            <p aria-hidden="true" className="text-chico text-texto-suave">
              {actual.alt}
            </p>
            <Row justificar="entre">
              <div>
                {hayAnterior && (
                  <Button variante="secundario" tamanio="sm" onClick={() => mover(-1)}>
                    Anterior
                  </Button>
                )}
              </div>
              <div>
                {haySiguiente && (
                  <Button variante="secundario" tamanio="sm" onClick={() => mover(1)}>
                    Siguiente
                  </Button>
                )}
              </div>
            </Row>
          </Stack>
        )}
      </Dialog>
    </>
  );
}
